import { useReactiveVar } from '@apollo/client'
import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import PopoverMenu from '@deltek/specification-client/core/Navigation/PopoverMenu'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    makeStyles,
    MenuItem,
    PopoverOrigin,
    Theme,
    Typography,
} from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { WorkspaceProjectElementNode } from 'api/graphQL/reactiveVars/element'
import { showBestPracticeSetting } from 'api/graphQL/reactiveVars/workspaceToggles'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import {
    useIsNotesMenuDisplayed,
    useSelectedMainMenuNode,
} from 'components/WorkspaceEditor/HoveredItemStore'
import {
    useSelectedProjectElementNode,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { WorkspaceActionBarContext } from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceActionBarContext'
import React, { useMemo } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'

export interface MenuItemStyleProps {
    hasSmallTitle?: boolean
    isBetaWorkspaceToggleEnabled?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    actionLabel: {
        fontSize: pxToRem(13),
        color: (props: MenuItemStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? '#424242' : '#A78966',
    },
    actionMenuItem: {
        padding: (props: MenuItemStyleProps) =>
            props.isBetaWorkspaceToggleEnabled
                ? theme.spacing(0.625, 0)
                : theme.spacing(0.5, 0),
        paddingRight: ({ hasSmallTitle }: MenuItemStyleProps) =>
            hasSmallTitle ? theme.spacing(6) : 'none',
    },
    smallLabel: {
        position: 'absolute',
        right: 0,
        fontSize: theme.spacing(1),
        color: theme.palette.grey[700],
    },
    menuText: {
        fontSize: pxToRem(12),
        color: '#211F20',
        cursor: 'pointer',
        marginTop: -theme.spacing(0.5),
    },
}))

export interface CompleteMenuItemConfig extends MenuItemConfig {
    isDisabled?: boolean
    isHidden?: boolean
    smallLabel?: string
}

/**
 * Props for the Workspace2Popover component.
 */
export interface Workspace2PopoverProps {
    /** The HTML element to which the popover is anchored. */
    anchorElement: Element | null
    /** Determines whether the action popover is displayed. */
    isActionPopoverDisplayed: boolean
    /** Optional origin point for the anchor of the popover. */
    anchorOrigin?: PopoverOrigin
    /** Optional custom CSS style for the text within the popover. */
    customTextStyle?: string
    /** Optional origin point for the transformation of the popover. */
    transformOrigin?: PopoverOrigin
    /** Optional flag to disable reviewer-related functionality. */
    disableReviewer?: boolean
    /** Callback function triggered when the action popover is clicked. */
    onCloseMainMenu: () => void
    /** Callback function triggered when the Best Practices menu is clicked. */
    onClickBPMenu: (e: any) => void
    /** Callback function triggered when the manage options menu is clicked. */
    onClickManageOptions: (e: any) => void
    /** Callback function triggered when the notes menu is clicked. */
    onClickNotesMenu: () => void
    /** Callback function triggered when the move block is clicked. */
    onClickMoveBlockMenu: (e: any) => void
}
/**
 * Generates the main menu items.
 */
export const getMainMenuItems = (
    classes: any,
    onClickManageOptions: (e: any) => void,
    onClickNotesMenu: () => void,
    onClickBPMenu: (e: any) => void,
    onClickMoveBlockMenu: (e: any) => void,
    isManageOptionsDisabled?: boolean,
    isBestPracticeMenuHidden?: boolean
): CompleteMenuItemConfig[] => [
    {
        dataTestId: 'manageOptions',
        label: 'Manage Options',
        isDisabled: isManageOptionsDisabled,
        component: (
            <GridContainer
                justifyContent="space-between"
                alignItems="center"
                onClick={
                    isManageOptionsDisabled ? undefined : onClickManageOptions
                }
                style={{
                    opacity: isManageOptionsDisabled ? 0.5 : 1,
                    cursor: isManageOptionsDisabled ? 'not-allowed' : 'pointer',
                }}
            >
                <GridItem>
                    <Typography
                        component="span"
                        className={classes.menuText}
                        style={{
                            color: isManageOptionsDisabled ? '#999' : undefined,
                        }}
                    >
                        Manage Options
                    </Typography>
                </GridItem>
                <GridItem>
                    <ChevronRightIcon
                        fontSize="small"
                        htmlColor={
                            isManageOptionsDisabled ? '#C0C0C0' : '#858585'
                        }
                    />
                </GridItem>
            </GridContainer>
        ),
    },
    {
        dataTestId: 'notes',
        label: 'Notes',
        isHidden: processEnv.REACT_APP_ShowNotesButtonInDragIconMenu === '0',
        component: (
            <GridContainer
                justifyContent="space-between"
                alignItems="center"
                onClick={onClickNotesMenu}
            >
                <GridItem>
                    <Typography component="span" className={classes.menuText}>
                        Notes
                    </Typography>
                </GridItem>
                <GridItem>
                    <ChevronRightIcon fontSize="small" htmlColor="#858585" />
                </GridItem>
            </GridContainer>
        ),
    },
    {
        dataTestId: 'bestPractice',
        label: 'Best Practices',
        isHidden:
            processEnv.REACT_APP_ShowBestPracticesFeatureInNewWorkspace ===
                '0' || isBestPracticeMenuHidden,
        component: (
            <GridContainer
                justifyContent="space-between"
                alignItems="center"
                onClick={onClickBPMenu}
            >
                <GridItem>
                    <Typography component="span" className={classes.menuText}>
                        Best Practices
                    </Typography>
                </GridItem>
                <GridItem>
                    <ChevronRightIcon fontSize="small" htmlColor="#858585" />
                </GridItem>
            </GridContainer>
        ),
    },
    {
        dataTestId: 'moveblock',
        label: 'Move Block',
        component: (
            <GridContainer
                justifyContent="space-between"
                alignItems="center"
                onClick={onClickMoveBlockMenu}
            >
                <GridItem>
                    <Typography component="span" className={classes.menuText}>
                        Move Block
                    </Typography>
                </GridItem>
                <GridItem>
                    <ChevronRightIcon fontSize="small" htmlColor="#858585" />
                </GridItem>
            </GridContainer>
        ),
    },
]

/**
 * Generates the notes menu items.
 */
export const getNotesMenuItems = (
    classes: any,
    onClickNotesMenu: () => void
): CompleteMenuItemConfig[] => [
    {
        dataTestId: 'backToMain',
        label: 'Main Menu',
        component: (
            <GridContainer
                justifyContent="flex-start"
                alignItems="center"
                onClick={onClickNotesMenu}
            >
                <GridItem
                    style={{
                        maxHeight: pxToRem(24),
                        left: pxToRem(3),
                        position: 'absolute',
                    }}
                >
                    <ChevronLeftIcon fontSize="small" htmlColor="#858585" />
                </GridItem>
                <GridItem style={{ paddingLeft: 15 }}>
                    <Typography component="span" className={classes.menuText}>
                        Main Menu
                    </Typography>
                </GridItem>
            </GridContainer>
        ),
    },
    {
        dataTestId: 'allNotes',
        label: 'All Notes',
        onClick: () => console.log('View All Notes'),
    },
    {
        dataTestId: 'createNote',
        label: 'Create New Note',
        onClick: () => console.log('Create New Note'),
    },
]
export const Workspace2Popover = ({
    anchorElement,
    isActionPopoverDisplayed,
    onCloseMainMenu,
    anchorOrigin,
    customTextStyle,
    transformOrigin,
    disableReviewer,
    onClickBPMenu,
    onClickManageOptions,
    onClickNotesMenu,
    onClickMoveBlockMenu,
}: Workspace2PopoverProps) => {
    const { currentState } = React.useContext(WorkspaceActionBarContext)
    const { previewFeatures } = usePreviewFeatures()
    // checking if the redesigned toggle on show/hide bar is enabled
    const isBetaWorkspaceToggleEnabled =
        previewFeatures.hasNewWorkspace2 &&
        currentState?.showHideStates?.betaWorkspace
    const classes = useStyles({
        hasSmallTitle: false,
        isBetaWorkspaceToggleEnabled: isBetaWorkspaceToggleEnabled,
    })
    const showBPSetting = useReactiveVar(showBestPracticeSetting)
    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()

    const isNotesMenuDisplayed = useIsNotesMenuDisplayed()

    // Get the selected node to check if it has manageable options
    const selectedMainMenuNode = useSelectedMainMenuNode()

    // Get contentItemViewNodes and expandedKeys to check parent's isUserAdded status
    const contentItemViewNodes = useWorkspace2Store(
        (state) => state.contentItemViewNodes
    )
    const expandedKeys = useWorkspace2Store((state) => state.expandedKeys)
    const selectedProjectElementNode = useSelectedProjectElementNode()
    const isFromPPLTemplate = (selectedProjectElementNode as WorkspaceProjectElementNode)?.isFromPPLTemplate ?? false

    // Memoize the element type checking logic separately for better performance
    const hasManageableElementTypes = useMemo(() => {
        if (
            !selectedMainMenuNode?.elements ||
            !Array.isArray(selectedMainMenuNode.elements)
        ) {
            return false
        }

        // Check if any element is a manageable type (pill, option group, etc.)
        return selectedMainMenuNode.elements.some((element: any) =>
            [
                NODE_TYPES.SelectionOption,
                NODE_TYPES.OptionGroup,
                NODE_TYPES.ReplacementOption,
            ].includes(element.type)
        )
    }, [selectedMainMenuNode?.elements])

    // Simple negation of the manageable elements check
    const isManageOptionsDisabled = !hasManageableElementTypes

    /**
     * Check if Best Practices menu should be hidden for user-added content.
     * Hide BP menu when:
     * 1. Smart Project Copy feature is disabled (REACT_APP_EnableSmartProjectCopy !== '1') and content is user-added
     * 2. Current node is a child of user-added content (parent.isUserAdded = true)
     * 3. Current node is a user-added article that is not expanded
     * 4. Current node is PPL Added Family or PT. These are Project Level elements only.
     */
    const isBestPracticeMenuHidden = useMemo(() => {
        if (!showBPSetting) return true
        if (isFromPPLTemplate) return true
        if (isCurrentlyOnOMWorkspace) return true
        if (!selectedMainMenuNode) return false

        const nodeId =
            selectedMainMenuNode.dtvContentItemId || selectedMainMenuNode.id
        const currentNode = contentItemViewNodes.find(
            (node) => node.id === nodeId
        )

        if (!currentNode) return false

        // Case 1: If Smart Project Copy feature is disabled, hide BP menu for all user-added content
        // This matches the classical workspace behavior in HoveredItemActions.tsx
        if (
            processEnv.REACT_APP_EnableSmartProjectCopy !== '1' &&
            currentNode.isUserAdded
        ) {
            return true
        }

        // Case 2: Check if parent is user-added
        // If parent is user-added, hide BP menu on children
        const parentId = currentNode.dtvContentItemParentId
        if (parentId) {
            const parentNode = contentItemViewNodes.find(
                (node) => node.id === parentId
            )
            if (parentNode?.isUserAdded) {
                return true
            }
        }

        // Case 3: If current node is user-added article and not expanded, hide BP menu
        // Check if node's id is in expandedKeys array (tree uses id as key)
        const isExpanded = expandedKeys.includes(nodeId as React.Key)
        if (
            currentNode.isUserAdded &&
            currentNode.textType === 'Article' &&
            !isExpanded
        ) {
            return true
        }

        return false
    }, [
        selectedMainMenuNode,
        contentItemViewNodes,
        expandedKeys,
        showBPSetting,
        isFromPPLTemplate,
        isCurrentlyOnOMWorkspace,
    ])

    const mainMenuItems = useMemo(
        () =>
            getMainMenuItems(
                classes,
                onClickManageOptions,
                onClickNotesMenu,
                onClickBPMenu,
                onClickMoveBlockMenu,
                isManageOptionsDisabled,
                isBestPracticeMenuHidden
            ),
        [
            classes,
            onClickManageOptions,
            onClickNotesMenu,
            onClickBPMenu,
            onClickMoveBlockMenu,
            isManageOptionsDisabled,
            isBestPracticeMenuHidden,
        ]
    )
    const notesMenuItems = useMemo(
        () => getNotesMenuItems(classes, onClickNotesMenu),
        [classes, onClickNotesMenu]
    )

    const menuItems = useMemo(() => {
        if (isNotesMenuDisplayed) {
            return notesMenuItems
        }

        const showNotesButton =
            processEnv.REACT_APP_ShowNotesButtonInDragIconMenu === '1'
        const showBestPracticesButton =
            processEnv.REACT_APP_ShowBestPracticesFeatureInNewWorkspace === '1'

        return mainMenuItems.filter((item) => {
            // Filter out items that are explicitly hidden
            if (item.isHidden) {
                return false
            }
            if (item.dataTestId === 'notes' && !showNotesButton) {
                return false
            }
            if (
                item.dataTestId === 'bestPractice' &&
                !showBestPracticesButton
            ) {
                return false
            }
            return true
        })
    }, [isNotesMenuDisplayed, mainMenuItems, notesMenuItems])

    return (
        <PopoverMenu
            open={isActionPopoverDisplayed}
            anchorEl={anchorElement}
            onClose={onCloseMainMenu}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            data-testid="workspace2Popover"
        >
            {menuItems.map(
                ({ dataTestId, component, label, onClick, isDisabled }) =>
                    component ?? (
                        <MenuItem
                            key={dataTestId}
                            onClick={onClick as () => void}
                            disabled={isDisabled || disableReviewer}
                            className={clsx(
                                customTextStyle,
                                classes.actionMenuItem,
                                classes.menuText
                            )}
                        >
                            {label}
                        </MenuItem>
                    )
            )}
        </PopoverMenu>
    )
}
